import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Trophy, Buildings, Star, FilePdf } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CaseStudiesPreview = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies`)
      .then(r => setCases(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = cases.slice(0, 3);
  const isEmpty = !loading && cases.length === 0;

  return (
    <section className="py-24 lg:py-32 bg-neutral-50" data-testid="case-studies-preview-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16">
          <div>
            <p className="accent-font text-lime-600 mb-4 inline-flex items-center gap-2">
              <Trophy size={18} weight="duotone" className="text-lime-600" />
              Proven Results
            </p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">
              Latest <span className="text-lime-600">Case Studies</span>
            </h2>
          </div>
          {!isEmpty && (
            <Link
              to="/case-studies"
              className="btn-secondary inline-flex items-center gap-2 mt-8 lg:mt-0"
              data-testid="case-studies-view-all"
            >
              View All Case Studies
              <ArrowRight size={20} weight="bold" />
            </Link>
          )}
        </div>

        {isEmpty ? (
          <div
            className="rounded-2xl border-2 border-dashed border-neutral-200 bg-white p-12 lg:p-16 text-center"
            data-testid="case-studies-empty-state"
          >
            <div className="inline-flex p-4 rounded-2xl bg-lime-50 mb-5">
              <Trophy size={36} weight="duotone" className="text-lime-600" />
            </div>
            <h3 className="font-outfit font-semibold text-2xl text-neutral-900 mb-3">
              Real results, coming soon
            </h3>
            <p className="text-neutral-600 max-w-xl mx-auto">
              We&apos;re putting the finishing touches to a series of customer case studies showcasing how
              NSEC delivers measurable results across waste collection, training, and highways.
              Check back shortly &mdash; or
              <Link to="/contact" className="text-lime-600 font-medium hover:underline ml-1">
                speak to our team
              </Link>{' '}
              about your project.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              [0, 1, 2].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden animate-pulse">
                  <div className="aspect-[16/10] bg-neutral-100" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 w-1/3 bg-neutral-100 rounded" />
                    <div className="h-5 w-4/5 bg-neutral-100 rounded" />
                    <div className="h-4 w-full bg-neutral-100 rounded" />
                    <div className="h-4 w-2/3 bg-neutral-100 rounded" />
                  </div>
                </div>
              ))
            ) : (
              featured.map((article, index) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={`/case-studies/${article.slug}`}
                  className="group block bg-white rounded-2xl border border-neutral-200 overflow-hidden hover:shadow-xl hover:border-lime-200 transition-all duration-300 h-full"
                  data-testid={`home-case-study-card-${article.slug}`}
                >
                  {article.image_url && (
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.client && (
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[11px] font-medium text-neutral-800 shadow-sm">
                          <Buildings size={12} weight="duotone" className="text-lime-600" />
                          {article.client}
                        </div>
                      )}
                      {article.featured && (
                        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-lime-600 text-white text-[10px] font-semibold shadow-md" data-testid={`home-featured-badge-${article.slug}`}>
                          <Star size={11} weight="fill" />
                          Featured
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-6 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.sector && (
                        <span className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-lime-100 text-lime-700">
                          {article.sector}
                        </span>
                      )}
                      {article.pdf_url && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-neutral-900 text-white">
                          <FilePdf size={11} weight="fill" /> PDF
                        </span>
                      )}
                    </div>
                    <h3 className="font-outfit font-semibold text-xl text-neutral-900 group-hover:text-lime-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-neutral-600 text-sm line-clamp-3">{article.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-lime-600 text-sm font-medium pt-1">
                      Read case study
                      <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))
          )}
            </div>
          )}
      </div>
    </section>
  );
};
